import React from 'react';
import { LogOut, Building2, Sparkles, ShieldCheck, Search, BookOpen } from 'lucide-react';
import CustomerSwitcher from './CustomerSwitcher';
import WhatsNew from './WhatsNew';

interface LinkedCustomer {
  customerNo?: string;
  customerId?: string;
  customerName?: string;
}

interface CurrentUser {
  email?: string;
  customerName?: string;
  customerId?: string;
  contactName?: string;
  availableTenants?: unknown[];
}

interface TenantSettings {
  branding?: {
    logoUrl?: string;
    logoBase64?: string;
    logoSource?: 'none' | 'url' | 'upload';
  };
}

interface AppHeaderProps {
  currentUser?: CurrentUser;
  appVersion: string;
  currentPage: string;
  userRole?: string;
  tenantSettings?: TenantSettings;
  linkedCustomers?: LinkedCustomer[] | null;
  onCustomerSwitch?: (customer: LinkedCustomer) => Promise<void>;
  onTenantSwitch: () => void;
  onMyAccount?: () => void;
  onHome?: () => void;
  onLogout: () => void;
  /** Opens Settings (two-step sign-in, preferences) */
  onSecurity?: () => void;
  /** Only passed for user role — triggers shell layout opt-in */
  onTryNewLayout?: () => void;
  /** Opens quick find (Ctrl-K / ⌘K) */
  onQuickFind?: () => void;
  /** UX-GROWTH H2: opens the help centre */
  onHelp?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  currentUser,
  appVersion,
  currentPage,
  userRole,
  tenantSettings,
  linkedCustomers,
  onCustomerSwitch,
  onTenantSwitch,
  onHome,
  onLogout,
  onSecurity,
  onHelp,
  onTryNewLayout,
  onQuickFind,
}) => {
  const isAdmin = userRole === 'global_admin' || userRole === 'reseller_admin' || userRole === 'tenant_admin';
  const getHeaderTitle = (): string => {
    if (currentPage === 'glbadm') {
      return 'Global Admin Portal';
    }
    if (currentPage === 'rsladm') {
      return 'Reseller Admin Portal';
    }
    if (currentPage === 'tntadm') {
      return 'Tenant Admin Portal';
    }
    return 'Goodstuff Portal';
  };

  const getTenantLogo = (): string | null => {
    const branding = tenantSettings?.branding;
    if (!branding) return null;

    const { logoUrl, logoBase64, logoSource = 'none' } = branding;

    // If none selected, no logo
    if (logoSource === 'none') return null;

    // Use the selected source
    if (logoSource === 'url' && logoUrl) return logoUrl;
    if (logoSource === 'upload' && logoBase64) return logoBase64;

    return null;
  };

  const logoSrc = getTenantLogo();

  return (
    <header className="bg-linear-to-br from-header-from to-primary-dark text-white py-5 shadow-md">
      <div className="max-w-7xl mx-auto px-5 flex justify-between items-center">
        <div
          className="flex items-center gap-4 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={onHome}
          title="Return to home"
        >
          {logoSrc && (
            <img
              src={logoSrc}
              alt="Company Logo"
              className="h-10 w-auto max-w-[150px] object-contain"
            />
          )}
          <div>
            <h1 className="text-xl font-semibold m-0">
              {/* The row above is a mouse-only click target; this gives keyboard users the same "home" action. */}
              {onHome ? (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onHome(); }}
                  className="bg-transparent p-0 text-inherit font-[inherit] text-left cursor-pointer rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                  {getHeaderTitle()}
                </button>
              ) : getHeaderTitle()}
            </h1>
            <div className="text-sm mt-1 text-white/85 font-medium flex items-center gap-2">
              v{appVersion}
              {currentUser && <WhatsNew role={userRole} appVersion={appVersion} />}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {currentUser && (
            <div className="flex items-center gap-3">
              {currentUser && currentUser.availableTenants && currentUser.availableTenants.length > 1 && (
                <button
                  onClick={onTenantSwitch}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/10 px-3 py-2 rounded-md cursor-pointer transition-colors flex items-center gap-1.5"
                  title="Switch tenant"
                >
                  <Building2 className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">Tenants</span>
                </button>
              )}
              {!isAdmin && linkedCustomers && onCustomerSwitch && (
                <CustomerSwitcher
                  currentUser={currentUser}
                  linkedCustomers={linkedCustomers}
                  onCustomerSwitch={onCustomerSwitch}
                  dark
                />
              )}
              {onTryNewLayout && (
                <button
                  onClick={onTryNewLayout}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/10 px-3 py-2 rounded-md cursor-pointer transition-colors flex items-center gap-1.5"
                  title="Try new sidebar layout (beta)"
                >
                  <Sparkles className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">New layout</span>
                </button>
              )}
              {onQuickFind && (
                <button
                  onClick={onQuickFind}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/10 px-3 py-2 rounded-md cursor-pointer transition-colors flex items-center gap-1.5"
                  title="Quick find (Ctrl+K)"
                  aria-keyshortcuts="Control+K Meta+K"
                >
                  <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="hidden sm:inline">Search</span>
                  <span className="sr-only sm:hidden">Quick find</span>
                  <kbd aria-hidden="true" className="hidden lg:inline text-[10px] opacity-70 border border-white/20 rounded px-1">Ctrl K</kbd>
                </button>
              )}
              {onHelp && (
                <button
                  onClick={onHelp}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/10 px-3 py-2 rounded-md cursor-pointer transition-colors flex items-center gap-1.5"
                  title="Help articles"
                  aria-current={currentPage === 'help' ? 'page' : undefined}
                >
                  <BookOpen className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="hidden sm:inline">Help</span>
                  <span className="sr-only sm:hidden">Help</span>
                </button>
              )}
              {onSecurity && (
                <button
                  onClick={onSecurity}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/10 px-3 py-2 rounded-md cursor-pointer transition-colors flex items-center gap-1.5"
                  title="Settings and two-step sign-in"
                >
                  <ShieldCheck className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">Security</span>
                </button>
              )}
              <button
                onClick={onLogout}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/10 px-3 py-2 rounded-md cursor-pointer transition-colors flex items-center gap-1.5"
                title="Logout"
              >
                <LogOut className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
